import React from 'react';
import { Dimensions, KeyboardAvoidingView, Platform, View } from 'react-native';
import { useIsKeyboardRestrictedView, useWindowHeightWithKeyboard } from './KeyboardRestrictedView';

/**
 * App-wide keyboard avoidance.
 *
 * iOS: RN's KeyboardAvoidingView (behavior="padding") is reliable, so use it.
 *
 * Android under mandatory edge-to-edge (Expo SDK 55, PR-2124): RN's KeyboardAvoidingView
 * leaves residual bottom padding after the keyboard closes (content stays shifted up, white gap
 * at the bottom, popovers mis-anchored). Instead, drive the padding from KeyboardRestrictedView's
 * keyboard-height signal, which resets cleanly to 0 on keyboardDidHide.
 *
 * MUST render BELOW SafeAreaProvider so the padding never shrinks the provider's measured frame
 * (that was the original Bug A — the root KeyboardAvoidingView wrapping SafeAreaProvider).
 */
const KeyboardAvoider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isKeyboardVisible = useIsKeyboardRestrictedView();
    const windowHeightWithKeyboard = useWindowHeightWithKeyboard();

    if (Platform.OS === 'ios') {
        return (
            <KeyboardAvoidingView behavior="padding" style={{ flex: 1, alignSelf: 'stretch' }}>
                {children}
            </KeyboardAvoidingView>
        );
    }

    const keyboardHeight = isKeyboardVisible
        ? Math.max(Dimensions.get('window').height - windowHeightWithKeyboard, 0)
        : 0;

    return <View style={{ flex: 1, alignSelf: 'stretch', paddingBottom: keyboardHeight }}>{children}</View>;
};

export default KeyboardAvoider;
